import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocalStorage, useMedia, useLocation } from 'react-use';

import PropTypes from 'prop-types';
import themeable from 'zignal-landscape/lib/themeable';
import find from 'lodash/find';
import noop from 'lodash/noop';
import hasIn from 'lodash/hasIn';
import compact from 'lodash/compact';
import classnames from 'classnames';
import Modal from 'zignal-landscape/lib/Modal';
// Selection Modals
import DashboardModal from '../SelectionModal/components/DashboardModal';
import ReportModal from '../SelectionModal/components/ReportModal';
import AlertModal from '../SelectionModal/components/AlertModal';
import ReadingListModal from '../SelectionModal/components/ReadingListModal';
import SavedSearchModal from '../SelectionModal/components/SavedSearchModal';
import CuratedDatasetModal from '../SelectionModal/components/CuratedDatasetModal';
// icons
import MfeSidebarChatIcon from 'zignal-landscape/lib/Icon/MfeSidebarChatIcon';
import MfeNavNotificationsIcon from 'zignal-landscape/lib/Icon/MfeNavNotificationsIcon';
import MfeSidebarHomeIcon from 'zignal-landscape/lib/Icon/MfeSidebarHomeIcon';
import MfeSidebarDiscoverIcon from 'zignal-landscape/lib/Icon/MfeSidebarDiscoverIcon';
import MfeSidebarDetectIcon from 'zignal-landscape/lib/Icon/MfeSidebarDetectIcon';
import MfeSidebarNarrativesIcon from 'zignal-landscape/lib/Icon/MfeSidebarNarrativesIcon';
import MfeSidebarDashboardsIcon from 'zignal-landscape/lib/Icon/MfeSidebarDashboardsIcon';
import MfeSidebarNewsroomIcon from 'zignal-landscape/lib/Icon/MfeSidebarNewsroomIcon';
import MfeSidebarZiiIcon from 'zignal-landscape/lib/Icon/MfeSidebarZiiIcon';
import MfeSidebarDataIcon from 'zignal-landscape/lib/Icon/MfeSidebarDataIcon';
import MfeSidebarReportingIcon from 'zignal-landscape/lib/Icon/MfeSidebarReportingIcon';
import MfeSidebarSettingsIcon from 'zignal-landscape/lib/Icon/MfeSidebarSettingsIcon';
import ChevronRightIcon from 'zignal-landscape/lib/Icon/ChevronRightIcon';
import ArrowLeftIcon from 'zignal-landscape/lib/Icon/ArrowLeftIcon';
import ArrowRightIcon from 'zignal-landscape/lib/Icon/ArrowRightIcon';

const RESPONSIVE = 'responsive';
const DESKTOP = 'desktop';
// DATA FLYOUT OPTIONS
const DATASETS = { label: 'Datasets', link: '/data/campaigns/<campaignId>/profiles/' };
const SMART_FILTERS = { label: 'Smart Filters', link: '/data/campaigns/<campaignId>/dashboard-filters/' };
const TAGS = { label: 'Tags', link: '/data/campaigns/<campaignId>/issues/' };
const GROUPS = { label: 'Groups', link: '/data/campaigns/<campaignId>/groups/' };
// REPORTING FLYOUT OPTIONS
const ALERT_RULES = { label: 'Alert Rules', link: '/alerts/', selectionModalType: 'alert' };
const REPORTS = { label: 'Reports', link: '/report/', selectionModalType: 'report' };
const READING_LISTS = { label: 'Reading Lists', link: '/reading-lists/', selectionModalType: 'readingList' };
const BRIEFINGS = { label: 'Briefings', link: '/briefings/' };
const CLIP_LIBRARY = { label: 'Clip Library', link: '/clip-library/' };
// SETTINGS FLYOUT OPTIONS
const MY_SETTINGS = { label: 'My Settings', link: '/profile/' };
const WORKSPACE_SETTINGS = { label: 'Workspace Settings', link: '/settings/' };
const ACCOUNT_SETTINGS = { label: 'Account Settings', link: '/settings/account/' };
const ADMIN_SETTINGS = { label: 'Admin Settings', link: '/settings/admin/' };

// This will find the first link that matches. Order matters.
const ALL_FLYOUT_LINKS = [
  DATASETS, SMART_FILTERS, TAGS, GROUPS, ALERT_RULES, REPORTS, READING_LISTS,
  BRIEFINGS, CLIP_LIBRARY, MY_SETTINGS, ACCOUNT_SETTINGS, ADMIN_SETTINGS,
  // keep this at the end of the list as it will match before account and admin settings
  WORKSPACE_SETTINGS
];

/*
 * Order and display of links in the sidebar
*/

const HOME = { Icon: MfeSidebarHomeIcon, label: 'Home', link: '/home/' };
// --- divider
// FIND
const INBOX = { Icon: MfeNavNotificationsIcon, label: 'Inbox', link: '/inbox/' };
const CHAT = { Icon: MfeSidebarChatIcon, label: 'Chat', hideIfDisabled: true, isBeta: true };
const DISCOVER = { Icon: MfeSidebarDiscoverIcon, label: 'Discover', link: '/discover/' };
const DETECT = { Icon: MfeSidebarDetectIcon, label: 'Detect', link: '/detect/' };
const NARRATIVES = { Icon: MfeSidebarNarrativesIcon, label: 'Narratives' };
// ANALYZE
const DASHBOARDS = { Icon: MfeSidebarDashboardsIcon, label: 'Dashboards', link: '/dashboard/', selectionModalType: 'dashboard' };
const NEWSROOM = { Icon: MfeSidebarNewsroomIcon, label: 'Newsroom', link: '/newsroom/' };
const INFLUENCE_INTELLIGENCE = { Icon: MfeSidebarZiiIcon, label: 'Influence Intelligence', link: '/influence-intelligence/' };
// MANAGE
const DATA = { Icon: MfeSidebarDataIcon, label: 'Data', match: ['/data/campaigns/'], flyoutOptions: [
  DATASETS,
  SMART_FILTERS,
  TAGS,
  GROUPS
]};
const REPORTING = { Icon: MfeSidebarReportingIcon, label: 'Reporting', match: ['/alerts/', '/report/', '/reading-lists/', '/briefings/', '/clip-library/' ], flyoutOptions: [
  ALERT_RULES,
  REPORTS,
  READING_LISTS,
  BRIEFINGS,
  CLIP_LIBRARY
]};
// --- divider
const SETTINGS = { Icon: MfeSidebarSettingsIcon, label: 'Settings', match: ['/settings/', '/profile/'], flyoutOptions: [
  MY_SETTINGS,
  WORKSPACE_SETTINGS,
  ACCOUNT_SETTINGS,
  ADMIN_SETTINGS
]};

// This will find the first link that matches, so order matters
const ALL_LINKS = [
  HOME, INBOX, CHAT, DISCOVER, DETECT, NARRATIVES, DASHBOARDS, NEWSROOM, INFLUENCE_INTELLIGENCE, DATA, REPORTING, SETTINGS
];
const checkActiveLabel = (location, campaignId, links) => {
  return (find(links, ({link='', match=[]}) => {
    if (!match.length) {
      match = [link];
    }
    return find(match, (str='') => {
      str = str.replace('<campaignId>', campaignId);
      return location.pathname.indexOf((str).slice(0, str.length - 1)) === 0;
    });
  }) || {}).label;
};

const DEFAULT_LINK_ACCESS = {};
const DEFAULT_EXTERNAL_LINK_HANDLERS = {};

const SideBar = ({
  styles,
  campaignId,
  linkAccess = DEFAULT_LINK_ACCESS,
  externalLinkHandlers = DEFAULT_EXTERNAL_LINK_HANDLERS,
  navigateBackBone = noop,
  newDashboardOrReport = noop,
  newAlert = noop,
  newReadingList = noop,
  newSavedSearch = noop,
  deleteModel = noop
}) => {
  // hooks
  const location = useLocation();
  const [navLocalStorage, setNavLocalStorage, removeNavLocalStorage] = useLocalStorage('navigation_state', 'null', { raw: true });
  const [flyout, setFlyout] = useState(null);
  const [selectionModalTypeOpen, setSelectionModalTypeOpen] = useState(null);
  const closeSelectionModal = useCallback(() => setSelectionModalTypeOpen(null), [setSelectionModalTypeOpen]);
  const [activeLabel, setActiveLabel] = useState('');
  const [activeFlyoutLabel, setActiveFlyoutLabel] = useState('');
  const [windowHeight, setWindowHeight] = useState(window?.innerHeight);
  const isMediaMobileView = useMedia('screen and (max-width: 1256px)');
  const isResponsive = navLocalStorage && navLocalStorage !== 'null' ? navLocalStorage === RESPONSIVE : isMediaMobileView;
  const isDesktop = !isResponsive;

  // this is a hack to get the selection modal open from the dashboard/report page and newsroom.
  window.__setSelectionModalTypeOpen = useCallback((arg) => {
    setSelectionModalTypeOpen(arg);
  }, [setSelectionModalTypeOpen]);

  const updateDimensions = () => setWindowHeight(window?.innerHeight);
  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // effect for changing body css when responsive state changes
  useEffect(() => {
    window.$('body')[ isResponsive ? 'addClass' : 'removeClass']('thinner-nav');
  }, [isResponsive]);

  useEffect(() => {
    const newActiveLabel = checkActiveLabel(location, campaignId, ALL_LINKS);
    if (activeLabel !== newActiveLabel) {
      setActiveLabel(newActiveLabel);
    }
    if (flyout) {
      const newActiveFlyoutLabel = checkActiveLabel(location, campaignId, ALL_FLYOUT_LINKS);
      if (activeFlyoutLabel !== newActiveFlyoutLabel) {
        setActiveFlyoutLabel(newActiveFlyoutLabel);
      }
    }
  }, [location, campaignId, flyout]);

  // handles minimizing/maximizing logic, uses local storage
  const onArrowClick = useCallback(() => {
    // we either clear the local storage in these cases
    if ((isMediaMobileView && isDesktop) || (!isMediaMobileView && isResponsive)) {
      removeNavLocalStorage();
      // or set the local storage to the opposite of the current state
    } else {
      setNavLocalStorage(isResponsive ? DESKTOP : RESPONSIVE);
    }
  }, [isMediaMobileView, isDesktop, isResponsive, removeNavLocalStorage, setNavLocalStorage]);

  // child components
  const Divider = () => <div className={styles.SideBarDivider} />;
  const Category = ({label}) => <div className={styles.SideBarCategory}>{isDesktop ? label : ''}</div>;
  const SideBarLink = ({Icon, label, link, selectionModalType, flyoutOptions, hideIfDisabled = false, isBeta = false}) => {
    const active = activeLabel === label;
    // use the label to check access, but if it in not a property, default to true
    const enabled = !hasIn(linkAccess, label) || linkAccess[label];
    let sidebarLinkClassName = styles.SideBarLink;
    sidebarLinkClassName = active ? styles.SideBarLinkActive : sidebarLinkClassName;
    sidebarLinkClassName = !enabled ? styles.SideBarLinkDisabled : sidebarLinkClassName;

    const onClick = useCallback((e) => {
      if (enabled) {
        if (externalLinkHandlers[label]) {
          externalLinkHandlers[label](e);
        } else if (selectionModalType) {
          setSelectionModalTypeOpen(selectionModalType);
        } else {
          navigateBackBone(e);
        }
      }
    }, [enabled, selectionModalType, navigateBackBone, setSelectionModalTypeOpen]);
    const href = enabled && !selectionModalType ? link : null;

    if (!enabled && hideIfDisabled) {
      return null;
    }

    return (
      <a
        href={href}
        key={label}
        className={styles.SideBarLinkContainer}
        onClick={onClick}
      >
        <div className={sidebarLinkClassName}>
          <Icon className={styles.SideBarIcon} />
          { isDesktop ? <div className={styles.SideBarLabel}>{label}</div> : null }
          { isBeta ? <span className={styles.SideBarBetaLabel}>BETA</span> : null }
          { isDesktop && flyoutOptions
            ? <div className={styles.SidebarChevron}><ChevronRightIcon height={12} width={12} /></div>
            : null
          }
        </div>
      </a>
    );
  };
  const SideBarFlyout = ({Icon, label, flyoutOptions}) => {
    const ref = useRef(null);
    const [shouldSetFlyout, setShouldSetFlyout] = useState(null);
    // I would have liked to skip this useEffect and just use the onClick to set the flyout,
    // but we need to read the 'top' value from the ref and that is only accessible using useEffect.
    // So 'shouldSetFlyout' flag triggers the useEffect, which then calls the parent component's 'setFlyout'
    useEffect(() => {
      if (ref.current && shouldSetFlyout) {
        const rect = ref.current.getBoundingClientRect();
        setFlyout({label, flyoutOptions, top: rect.top});
        setShouldSetFlyout(null);
      }
    }, [shouldSetFlyout]);

    const active = activeLabel === label;
    const enabled = !hasIn(linkAccess, label) || linkAccess[label];
    const flyoutOpen = flyout && flyout.label === label;
    let sidebarLinkClassName = styles.SideBarLink;
    sidebarLinkClassName = active ? styles.SideBarLinkActive : sidebarLinkClassName;
    sidebarLinkClassName = !enabled ? styles.SideBarLinkDisabled : sidebarLinkClassName;
    sidebarLinkClassName = flyoutOpen ? styles.SideBarFlyoutOpen : sidebarLinkClassName;

    const onClick = useCallback(() => enabled && setShouldSetFlyout(true), [enabled, setShouldSetFlyout]);
    return (
      <div
        ref={ref}
        key={label}
        className={styles.SideBarLinkContainer}
        onClick={onClick}
      >
        <div className={sidebarLinkClassName}>
          <Icon className={styles.SideBarIcon} />
          { isDesktop ? <div className={styles.SideBarLabel}>{label}</div> : null }
          { isDesktop && flyoutOptions ? <div className={styles.SidebarChevron}><ChevronRightIcon height={12} width={12} /></div> : null }
        </div>
      </div>
    );
  };
  const FlyoutModal = () => {
    if (!flyout) {
      return null;
    }
    const closeFlyout = useCallback(() => setFlyout(null), [setFlyout]);
    const navigateFlyoutBackBone = useCallback((e) => {
      closeFlyout();
      navigateBackBone(e);
    }, [closeFlyout]);

    return (<Modal
      isOpen
      hasBackground={false}
      hasTransparentBackdrop
      onBackdropClick={closeFlyout}
    >
      <div style={flyoutStyles} className={styles.SideBarFlyout}>
        {flyout.flyoutOptions.map(({label, link='', selectionModalType}) => {
          const active = activeFlyoutLabel === label;
          const enabled = !hasIn(linkAccess, label) || linkAccess[label];

          const onClick = useCallback((e) => {
            if (enabled) {
              if (selectionModalType) {
                closeFlyout();
                setSelectionModalTypeOpen(selectionModalType);
              } else {
                navigateFlyoutBackBone(e);
              }
            }
          }, [enabled, selectionModalType, closeFlyout, navigateFlyoutBackBone, setSelectionModalTypeOpen]);

          let sideBarFlyoutOptionClassName = styles.SideBarFlyoutOption;
          sideBarFlyoutOptionClassName = active ? styles.SideBarFlyoutOptionActive : sideBarFlyoutOptionClassName;
          sideBarFlyoutOptionClassName = !enabled ? styles.SideBarFlyoutOptionDisabled : sideBarFlyoutOptionClassName;

          link = link.replace('<campaignId>', campaignId);
          const href = enabled && !selectionModalType ? link : null;
          return (<a
            className={sideBarFlyoutOptionClassName}
            href={href}
            key={label}
            onClick={onClick}
          >
            {label}
          </a>);
        })}
      </div>
    </Modal>);
  };
  const Minimizer = () => {
    const props = { height: 30, width: 30, onClick: onArrowClick };
    const ArrowIcon = isDesktop ? ArrowLeftIcon : ArrowRightIcon;
    return (<div className={styles.SideBarMinimizer}>
      <ArrowIcon {...props} />
    </div>);
  };

  const sidebarClassNames = { [styles.SideBar]: true, [styles.SideBarResponsive]: isResponsive };
  // offset the top by 10px to adjust for sidebar link padding
  const flyoutStyles = flyout ? { top: flyout.top + 10, left: 200 } : {};
  if (flyout) {
    if (isResponsive) {
      flyoutStyles.left = 41;
    }
    const flyoutHeight = flyout.flyoutOptions.length * 30 + 42;
    if (flyoutStyles.top + flyoutHeight > windowHeight) {
      // if the flyout will be out of view, adjust the top to fit in the window
      flyoutStyles.top = flyout.top - flyoutHeight + 50;
    }
  }

  const selectionModalProps = {
    campaignId,
    navigateBackBone,
    newDashboardOrReport,
    newAlert,
    newReadingList,
    newSavedSearch,
    deleteModel,
    onClose: closeSelectionModal,
    params: compact(location.pathname.split('/'))
  };
  return (
    <div className={classnames(sidebarClassNames)} data-cy="SideBar">
      <div className={styles.SideBarMenu}>
        <SideBarLink {...HOME} />
        <Divider />
        <Category label="SEARCH" />
        <SideBarLink {...INBOX} />
        <SideBarLink {...CHAT} />
        <SideBarLink {...DISCOVER} />
        <SideBarLink {...DETECT} />
        <SideBarLink {...NARRATIVES} />
        <Category label="ANALYZE" />
        <SideBarLink {...DASHBOARDS} />
        <SideBarLink {...NEWSROOM} />
        <SideBarLink {...INFLUENCE_INTELLIGENCE} />
        <Category label="MANAGE" />
        <SideBarFlyout {...DATA} />
        <SideBarFlyout {...REPORTING} />
        <Divider />
        <SideBarFlyout {...SETTINGS} />
        <Minimizer />
      </div>
      <FlyoutModal />
      { selectionModalTypeOpen === 'dashboard' && <DashboardModal{...selectionModalProps} /> }
      { selectionModalTypeOpen === 'report' && <ReportModal {...selectionModalProps} /> }
      { selectionModalTypeOpen === 'alert' && <AlertModal {...selectionModalProps} /> }
      { selectionModalTypeOpen === 'readingList' && <ReadingListModal {...selectionModalProps} /> }
      { selectionModalTypeOpen === 'savedSearch' && <SavedSearchModal {...selectionModalProps} /> }
      { selectionModalTypeOpen === 'curatedDataset' && <CuratedDatasetModal {...selectionModalProps} /> }
    </div>
  );
};

SideBar.propTypes = {
  styles: PropTypes.object.isRequired,
  linkAccess: PropTypes.object,
  externalLinkHandlers: PropTypes.object,
  navigateBackBone: PropTypes.func,
  newDashboardOrReport: PropTypes.func,
  newAlert: PropTypes.func,
  newReadingList: PropTypes.func,
  newSavedSearch: PropTypes.func,
  deleteModel: PropTypes.func
};

export default themeable(SideBar);
